// Левая колонка — библиотека блоков: поиск, группировка по типу, добавление.
import { useEffect, useId, useMemo, useState } from 'react';
import type { FlightSegment, Segment, SegmentKind } from '../../types';
import { useActions, useStore } from '../../store';
import { useTrips } from '../../trips';
import { PriceRefreshButton } from '../../prices';
import { BlockPresets, buildBlock, type BlockSpec } from '../../templates';
import { ParseModal } from '../parse/SourceModal';
import { SegmentFormModal } from '../forms';
import { useIsMobile } from '../shell';
import { SegmentCard } from './SegmentCard';
import {
  clearSegmentReads,
  isNewSegment,
  loadReadIds,
  loadSeenAt,
  markSegmentRead,
  markSeen,
  sortByAddedDesc,
} from './newFlags';
import { loadAccordionOpen, saveAccordionOpen } from './mobileAccordion';
import { useT } from '../../i18n';
import styles from './LibraryPanel.module.css';

/** Порядок групп в библиотеке; подпись берётся из словаря по ключу library.group.*. */
const GROUP_KINDS: readonly SegmentKind[] = [
  'flight',
  'transfer',
  'lodging',
  'taxi',
  'food',
  'baggage',
  'misc',
];

function searchableText(seg: Segment): string {
  const parts: Array<string | undefined> = [];
  switch (seg.kind) {
    case 'flight':
      parts.push(seg.from, seg.to, seg.carrier, seg.flightNumber, seg.note);
      break;
    case 'transfer':
      parts.push(seg.from, seg.to, seg.note);
      break;
    case 'lodging':
      parts.push(seg.city, seg.note);
      break;
    case 'taxi':
    case 'food':
    case 'misc':
      parts.push(seg.label, seg.city, seg.note);
      break;
    case 'baggage':
      parts.push(seg.label);
      break;
  }
  return parts.filter(Boolean).join(' ').toLowerCase();
}

interface FormState {
  mode: 'create' | 'edit';
  kind?: SegmentKind;
  segmentId?: string;
}

export function LibraryPanel() {
  const state = useStore();
  const actions = useActions();
  const t = useT();
  const trips = useTrips();
  const [query, setQuery] = useState('');
  const [collapsed, setCollapsed] = useState<Set<SegmentKind>>(new Set());
  const [form, setForm] = useState<FormState | null>(null);
  const [parseOpen, setParseOpen] = useState(false);

  // D11: на мобильном панель — свой аккордеон, а не всегда развёрнутая
  // колонка (App.tsx больше не форсирует её открытой). Дефолт — от того,
  // есть ли уже варианты: пустая поездка начинается с открытой библиотеки
  // (собирать блоки), а у обжитой поездки библиотека уступает место канвасу.
  // Дальше выбор человека помнится на сессию (см. mobileAccordion.ts).
  const isMobile = useIsMobile();
  const scrollId = useId();
  const [accordionOpen, setAccordionOpen] = useState(() =>
    loadAccordionOpen('library', state.variants.length === 0),
  );
  const showBody = !isMobile || accordionOpen;
  function toggleAccordion() {
    setAccordionOpen((prev) => {
      const next = !prev;
      saveAccordionOpen('library', next);
      return next;
    });
  }

  // Метка «просмотрено» персональна и живёт в localStorage, не в состоянии
  // поездки: у гостя (нет поездок на сервере) она общая под ключом 'local'.
  const seenScope = trips.currentId ?? 'local';
  const [seenAt, setSeenAt] = useState(() => loadSeenAt(seenScope));
  // Клик по новому блоку гасит подсветку у него одного — отметки хранятся
  // поимённо, рядом с общей меткой «просмотрено всё».
  const [readIds, setReadIds] = useState<ReadonlySet<string>>(() =>
    loadReadIds(seenScope, Date.now()),
  );
  useEffect(() => {
    setSeenAt(loadSeenAt(seenScope));
    setReadIds(loadReadIds(seenScope, Date.now()));
  }, [seenScope]);

  const allSegments = useMemo(() => Object.values(state.library), [state.library]);

  // Все рейсы библиотеки — их цены кнопка в шапке проверяет одним заходом.
  // Именно все, а не отфильтрованные поиском: поиск сужает то, что видно, а не
  // то, что человек просит проверить.
  const flights = useMemo(
    () => allSegments.filter((seg): seg is FlightSegment => seg.kind === 'flight'),
    [allSegments],
  );
  const trimmedQuery = query.trim().toLowerCase();

  const filtered = useMemo(() => {
    if (!trimmedQuery) return allSegments;
    return allSegments.filter((s) => searchableText(s).includes(trimmedQuery));
  }, [allSegments, trimmedQuery]);

  // now берётся один раз на рендер: недельное окно «новизны» не нуждается в
  // тикающем таймере, а стабильность в пределах рендера важна для сортировки.
  const now = Date.now();
  const newCount = useMemo(
    () => allSegments.filter((s) => isNewSegment(s, seenAt, now, readIds)).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allSegments, seenAt, readIds],
  );

  const groups = useMemo(() => {
    const byKind = new Map<SegmentKind, Segment[]>();
    for (const seg of filtered) {
      const list = byKind.get(seg.kind);
      if (list) list.push(seg);
      else byKind.set(seg.kind, [seg]);
    }
    return GROUP_KINDS.map((kind) => ({ kind, items: sortByAddedDesc(byKind.get(kind) ?? []) })).filter(
      (g) => g.items.length > 0,
    );
  }, [filtered]);

  function toggleGroup(kind: SegmentKind) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(kind)) next.delete(kind);
      else next.add(kind);
      return next;
    });
  }

  function openEdit(id: string) {
    setForm({ mode: 'edit', segmentId: id });
  }

  function openCreate() {
    setForm({ mode: 'create' });
  }

  function dismissNew() {
    const at = Date.now();
    markSeen(seenScope, at);
    clearSegmentReads(seenScope);
    setSeenAt(at);
    setReadIds(new Set());
  }

  /** Карточка зовёт это, только пока она новая: клики по старым блокам
   *  хранилище не раздувают. */
  function markRead(id: string) {
    markSegmentRead(seenScope, id, Date.now());
    setReadIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }

  /** Частый блок ложится в библиотеку сразу: цена нулевая, править — как обычно. */
  function addPreset(spec: BlockSpec) {
    actions.addSegment(buildBlock(spec, t, state.displayCurrency));
  }

  // Свёрнутая на мобильном карточка — это только шапка-кнопка, без строки под
  // ней: нижняя обводка .header иначе рисовала бы вторую границу впритык к
  // обводке самой карточки (App.module.css/.library) — двойная линия там,
  // где её никто не ждёт.
  const headerClass = [styles.header, showBody ? '' : styles.headerCollapsed]
    .filter(Boolean)
    .join(' ');

  return (
    <aside className={styles.panel} aria-label={t('app.library')}>
      <div className={headerClass}>
        {/* На мобильном шапка сама — кнопка аккордеона (D11): App.tsx больше не
            держит панель принудительно открытой, сворачиванием управляет сама
            панель. На десктопе это по-прежнему обычная строка без интерактива —
            там сворачиванием занята кнопка на шве (App.tsx). */}
        {isMobile ? (
          <button
            type="button"
            className={styles.accordionHead}
            aria-expanded={accordionOpen}
            aria-controls={scrollId}
            onClick={toggleAccordion}
          >
            <span className={styles.accordionTitle}>
              {t('library.title')} · {t('library.count', { count: allSegments.length })}
            </span>
            <svg
              className={`${styles.chevron} ${accordionOpen ? '' : styles.collapsed}`}
              width="10"
              height="10"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.4}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
              focusable="false"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
        ) : (
          <div className={styles.headRow}>
            <span className={styles.headTitle}>{t('library.title')}</span>
            <span className={styles.headCount}>
              {t('library.count', { count: allSegments.length })}
            </span>
          </div>
        )}
        {showBody && (
          <>
            {newCount > 0 && (
              <div className={styles.newRow}>
                <span className={styles.newCount}>{t('library.new.count', { count: newCount })}</span>
                <button type="button" className="btn btn-sm" onClick={dismissNew}>
                  {t('library.new.dismiss')}
                </button>
              </div>
            )}
            <div className={styles.actionsRow}>
              <button type="button" className={`btn btn-primary ${styles.actionBtn}`} onClick={openCreate}>
                {t('library.add')}
              </button>
              {/* Проверка цен по всем рейсам библиотеки. Растянутой кнопкой
                  «+ Блок» она не становится: та начинает работу с библиотекой,
                  а эта — редкое действие поверх собранного. */}
              <PriceRefreshButton segments={flights} className={styles.priceBtn} />
            </div>
            {/* Частые блоки — второй, самый короткий путь к блоку; свёрнуты, пока
                в библиотеке что-то есть. «Из источника» здесь не показывается —
                вход в распознавание живёт на шаге выбора типа в «+ Блок»
                (см. onPickSource у SegmentFormModal ниже). */}
            <BlockPresets defaultOpen={allSegments.length === 0} onAdd={addPreset} />
            {allSegments.length > 0 && (
              <input
                type="search"
                className={`input ${styles.search}`}
                placeholder={t('library.search.placeholder')}
                aria-label={t('library.search')}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            )}
          </>
        )}
      </div>

      {showBody && (
        <div className={styles.scroll} id={scrollId}>
          {allSegments.length === 0 ? (
            <div className={styles.empty}>
              <p className={styles.emptyTitle}>{t('library.empty')}</p>
              <p className={styles.emptyText}>{t('library.empty.text')}</p>
              <div className={styles.emptyActions}>
                <button type="button" className="btn btn-primary" onClick={openCreate}>
                  {t('library.add')}
                </button>
                <button type="button" className="btn" onClick={() => setParseOpen(true)}>
                  {t('library.fromSource')}
                </button>
              </div>
            </div>
          ) : groups.length === 0 ? (
            <div className={styles.noResults}>
              <p>{t('library.noResults', { query: query.trim() })}</p>
              <button type="button" className="btn btn-sm" onClick={() => setQuery('')}>
                {t('library.resetSearch')}
              </button>
            </div>
          ) : (
            groups.map((g) => {
              const hasNew = g.items.some((seg) => isNewSegment(seg, seenAt, now, readIds));
              // Группа со свежими блоками не должна прятать их за свёрнутым
              // заголовком — ручное «свернуть» временно уступает подсветке новизны.
              const isCollapsed = collapsed.has(g.kind) && !hasNew;
              return (
                <div key={g.kind} className={styles.group}>
                  <button
                    type="button"
                    className={styles.groupHead}
                    aria-expanded={!isCollapsed}
                    onClick={() => toggleGroup(g.kind)}
                  >
                    <svg
                      className={`${styles.chevron} ${isCollapsed ? styles.collapsed : ''}`}
                      width="10"
                      height="10"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2.4}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                    <span>{t(`library.group.${g.kind}`)}</span>
                    <span className={styles.groupCount}>({g.items.length})</span>
                  </button>
                  {!isCollapsed && (
                    <ul className={styles.list}>
                      {g.items.map((seg) => {
                        const segIsNew = isNewSegment(seg, seenAt, now, readIds);
                        return (
                          <li key={seg.id}>
                            <SegmentCard
                              segment={seg}
                              draggable
                              onEdit={openEdit}
                              isNew={segIsNew}
                              onRead={segIsNew ? markRead : undefined}
                            />
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {form && (
        <SegmentFormModal
          open
          mode={form.mode}
          kind={form.kind}
          segmentId={form.segmentId}
          onClose={() => setForm(null)}
          onPickSource={() => {
            // Закрываем попап выбора типа и в том же коммите открываем
            // распознавание: SegmentFormModal зовёт это только на шаге выбора
            // типа, а фокус после размонтирования уходит прямо в ParseModal.
            setForm(null);
            setParseOpen(true);
          }}
        />
      )}
      <ParseModal open={parseOpen} onClose={() => setParseOpen(false)} />
    </aside>
  );
}
